type Links = {
  label: string;
  links: {
    linkText: string;
    link: string;
    connectingText?: string;
  }[];
};

type CareerOutlook = {
  title: string;
  description: string;
  icon?: string;
};

export interface IndustryHighlight {
  id: number;
  name: string;
  slug: string;
  careerPaths: string[];
  careerPathsLinks?: Links;
  careerOutlook: CareerOutlook[];
  careerOutlookLinks?: Links;
}

const industryHighlights: IndustryHighlight[] = [
  {
    id: 10,
    name: 'Art & Design',
    slug: 'art-and-design',
    careerPaths: [
      'Graphic Designers',
      'Art Directors',
      'Multimedia Artists and Animators',
      'Industrial Designers',
      'Interior Designers',
    ],

    careerPathsLinks: {
      label: 'See the entire list:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/arts-and-design/',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$53,180',
        description: 'Avg. salary in 2024',
        icon: 'piggy-bank',
      },
      {
        title: '$87,900',
        description: 'Estimated job openings each year',
        icon: 'dine-and-dash',
      },
    ],
    careerOutlookLinks: {
      label: 'Source',
      links: [{ linkText: 'Bureau of Labor Statistics', link: 'https://www.bls.gov/ooh/arts-and-design/' }],
    },
  },
  {
    id: 1,
    name: 'Business & Management',
    slug: 'business-and-management',
    careerPaths: [
      'General and Operations Managers',
      'Financial Managers',
      'Medical and Health Services Managers',
      'Market Research Analysts',
      'Management Analysts',
    ],

    careerPathsLinks: {
      label: 'See the entire list:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/business-and-financial/',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$80,920',
        description: 'Avg. salary in 2024',
        icon: 'piggy-bank',
      },
      {
        title: '963,500',
        description: 'Estimated job openings each year',
        icon: 'dine-and-dash',
      },
    ],

    careerOutlookLinks: {
      label: 'Source:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/business-and-financial/',
        },
      ],
    },
  },
  {
    id: 7,
    name: 'Computers & Technology',
    slug: 'computers-and-technology',
    careerPaths: [
      'Software Developers',
      'Information Security Analysts',
      'Computer Systems Analysts',
      'Network and Computer Systems Administrators',
      'Database Administrators',
    ],
    careerPathsLinks: {
      label: 'See the entire list:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/computer-and-information-technology/',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$105,990',
        description: 'Avg. salary in 2024',
        icon: 'piggy-bank',
      },
      {
        title: '356,700',
        description: 'Estimated job openings each year',
        icon: 'dine-and-dash',
      },
    ],
    careerOutlookLinks: {
      label: 'Source:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/computer-and-information-technology/',
        },
      ],
    },
  },
  {
    id: 2,
    name: 'Criminal Justice & Legal',
    slug: 'criminal-justice-and-legal',
    careerPaths: [
      'Police Officers',
      'Detectives and Criminal Investigators',
      'Paralegals and Legal Assistants',
      'Lawyers',
      'Correctional Officers and Bailiffs',
    ],
    careerPathsLinks: {
      label: 'Bureau of Labor Statistics:',
      links: [
        {
          linkText: 'Source 1',
          link: 'https://www.bls.gov/ooh/protective-service/police-and-detectives.htm#tab-8',
          connectingText: '&amp;',
        },
        {
          linkText: 'Source 2',
          link: 'https://www.bls.gov/ooh/legal/',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$77,270',
        description: 'Avg. salary in 2024 for law enforcement',
        icon: 'piggy-bank',
      },
      {
        title: '796,800',
        description: 'Est. job openings each year in law enforcement',
        icon: 'dine-and-dash',
      },
    ],
    careerOutlookLinks: {
      label: 'Source:',
      links: [{ linkText: 'Bureau of Labor Statistics', link: 'https://www.bls.gov/ooh/legal/home.htm' }],
    },
  },
  {
    id: 3,
    name: 'Education & Teaching',
    slug: 'education-and-teaching',
    careerPaths: [
      'Elementary School Teachers',
      'High School Teachers',
      'Postsecondary Teachers',
      'Special Education Teachers',
      'School Principals',
    ],
    careerPathsLinks: {
      label: 'See the entire list:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/education-training-and-library/',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$59,220',
        description: 'Avg. salary in 2024',
        icon: 'piggy-bank',
      },
      {
        title: '868,000',
        description: 'Estimated job openings each year',
        icon: 'dine-and-dash',
      },
    ],
    careerOutlookLinks: {
      label: 'Source:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/education-training-and-library/',
        },
      ],
    },
  },
  {
    id: 13,
    name: 'General',
    slug: 'general',
    careerPaths: [],
    careerOutlook: [],
  },
  {
    id: 8,
    name: 'Liberal Arts & Humanities',
    slug: 'liberal-arts-and-humanities',
    careerPaths: [
      'Writers and Authors',
      'Editors',
      'Public Relations Specialists',
      'Social and Community Service Managers',
      'Historians',
    ],
    careerPathsLinks: {
      label: 'See the entire list:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/field-of-degree/liberal-arts/liberal-arts-field-of-degree.htm#emp',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$55,000',
        description: 'Avg. salary in 2022',
        icon: 'piggy-bank',
      },
      {
        title: '51%',
        description: 'Employed in jobs requiring a bachelor’s or higher',
        icon: 'grad-cap-toss',
      },
    ],
    careerOutlookLinks: {
      label: 'Source:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/field-of-degree/liberal-arts/liberal-arts-field-of-degree.htm#emp',
        },
      ],
    },
  },
  {
    id: 5,
    name: 'Nursing & Healthcare',
    slug: 'nursing-and-healthcare',
    careerPaths: [
      'Registered Nurses',
      'Nurse Practitioners',
      'Physician Assistants',
      'Medical and Health Services Managers',
      'Physical Therapists',
    ],
    careerPathsLinks: {
      label: 'See the entire list:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/healthcare/#:~:text=Overall%20employment%20in%20healthcare%20occupations,who%20leave%20the%20occupations%20permanently',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$83,090',
        description: 'Avg. salary in 2024',
        icon: 'piggy-bank',
      },
      {
        title: '1.9 million',
        description: 'Estimated job openings each year',
        icon: 'dine-and-dash',
      },
    ],
    careerOutlookLinks: {
      label: 'Source:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/healthcare/#:~:text=Overall%20employment%20in%20healthcare%20occupations,who%20leave%20the%20occupations%20permanently',
        },
      ],
    },
  },
  {
    id: 11,
    name: 'Psychology & Counseling',
    slug: 'psychology-and-counseling',
    careerPaths: [
      'Substance Abuse, Behavioral Disorder, and Mental Health Counselors',
      'Grief, Anger, Sexual Assault, and Other Types of Counselors',
      'Marriage and Family Therapists',
      'Clinical and Counseling Psychologists',
      'Psychiatric Technicians',
    ],
    careerPathsLinks: {
      label: 'See the entire list:',
      links: [
        {
          linkText: 'Psychology.org',
          link: 'https://www.psychology.org/resources/fastest-growing-careers-in-psychology/',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$94,310',
        description: 'Avg. salary in 2024 for psychologists',
        icon: 'piggy-bank',
      },
      {
        title: '13,000',
        description: 'Estimated psychology job openings each year',
        icon: 'dine-and-dash',
      },
    ],
    careerOutlookLinks: {
      label: 'Source:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/ooh/life-physical-and-social-science/psychologists.htm#tab-1',
        },
      ],
    },
  },
  {
    id: 4,
    name: 'Science & Engineering',
    slug: 'science-and-engineering',
    careerPaths: [
      'Computer Hardware Engineer',
      'Petroleum Engineer',
      'Aerospace Engineer',
      'Nuclear Engineer',
      'Chemical Engineer',
    ],
    careerPathsLinks: {
      label: 'See the entire list:',
      links: [
        {
          linkText: 'BestColleges.com',
          link: 'https://www.bestcolleges.com/careers/science-and-engineering/',
        },
      ],
    },
    careerOutlook: [
      {
        title: '$103,580',
        description: 'Avg. salary in 2024 for careers in STEM',
        icon: 'piggy-bank',
      },
      {
        title: '10.4%',
        description: 'Avg. estimated increase in STEM positions by 2033',
        icon: 'arrow-to-clouds',
      },
    ],
    careerOutlookLinks: {
      label: 'Source:',
      links: [
        {
          linkText: 'Bureau of Labor Statistics',
          link: 'https://www.bls.gov/emp/tables/stem-employment.htm#BLStable_2024_7_29_10_34_footnotes',
        },
      ],
    },
  },
  {
    id: 9,
    name: 'Trades & Careers',
    slug: 'trades-and-careers',
    careerPaths: [],
    careerOutlook: [],
  },
];

export default industryHighlights;
